import React, { useContext, useEffect, useRef, useState } from "react";
import { Rows } from "./Row";
import {
    CardTableRows,
    CardTableSelectAllControl,
    CardTableShell,
    CardTableToolbar,
    FlexContainer,
    LottieLoadingIndicator,
    Toast
} from "src/components";
import Checkbox from "src/scenes/EmployeeList/components/Checkbox";
import { ActionButton, TextLabel } from "../../../components/style";

import { useTableHooks } from "../../../hooks/useTableHooks";
import HeaderCard from "src/scenes/EmailCampaigns/components/HeaderCard";
import { EmailCampaignsContext } from "src/scenes/EmailCampaigns/EmailCampaignsContext";
import { CsvFileHooks } from "src/scenes/EmailCampaigns/hooks/csvFileHooks";
import AddSingleContactForm from "src/scenes/EmailCampaigns/components/audience/AddSingleContactForm";
import { LoadingIndicatorButton } from "src/scenes/Survey/components/Utils";
import { saveAudience } from "src/scenes/EmailCampaigns/api/audienceApi";
import { UserContext } from "src/scenes/App/UserContext";
import { CONTACT_ADDING_TYPE } from "src/scenes/EmailCampaigns/constants";

export const UploadNewContactsTable = () => {
    const { countryCode } = useContext(UserContext);
    const { uploadedCsvData, setUploadedCsvData, audienceName, setAudienceName, initialFetchSettings } =
        useContext(EmailCampaignsContext);
    const [isLoading, setIsLoading] = useState(false);
    const fileInputRef = useRef(null);
    const [showAddSingleContact, setShowAddSingleContact] = useState(false);
    const [contactFormData, setContactFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        mobileNumber: "",
        reference: "",
    });
    const [contactsList, setContactsList] = useState([]);
    const [newContactList, setNewContactList] = useState([]);
    const [manuallyAddedContacts, setManuallyAddedContacts] = useState([]);

    const { handleCsvUpload } = CsvFileHooks(setUploadedCsvData);
    const onImportCsvClick = () => {
        fileInputRef.current.click();
    };

    const onSaveSingleContact = () => {
        const exists = newContactList.some((contact) => contact.email === contactFormData.email);
        if (exists) return Toast.error("Contact with this email already exist.");
        setNewContactList((prev) => [...prev, { ...contactFormData, type: CONTACT_ADDING_TYPE.MANUALLY_ADDED }]);
        setManuallyAddedContacts((prev) => [...prev, { ...contactFormData, type: CONTACT_ADDING_TYPE.MANUALLY_ADDED }]);
        setShowAddSingleContact(!showAddSingleContact);
        setContactFormData({
            firstName: "",
            lastName: "",
            email: "",
            mobileNumber: "",
            reference: "",
        });
    };

    useEffect(() => {
        setNewContactList((prev) => [...uploadedCsvData, ...manuallyAddedContacts]);
    }, [uploadedCsvData, manuallyAddedContacts]);

    const toggleAddSingleContact = () => {
        console.log("test1");
        setShowAddSingleContact(!showAddSingleContact);
    };

    const SortableColumns = {
        select: 0,
        firstName: 1,
        lastName: 2,
        email: 3,
        mobile: 4,
        reference: 5,
    };

    const {
        tableData,
        selectedItem,
        selectAllItem,
        handleMainCheckbox,
        toggleItemSelection,
        isItemSelected,
        handleSortColumn,
        handleDeleteSelectedItems,
    } = useTableHooks(
        newContactList,
        SortableColumns,
        contactsList,
        setContactsList,
        uploadedCsvData,
        setUploadedCsvData,
        manuallyAddedContacts,
        setManuallyAddedContacts
    );

    const isASortableColumn = (index) => index > 0;

    const enableButton = tableData.every(contact => contact.isDuplicate === false);

    const onSaveAudienceAndContacts = async () => {
        setIsLoading(true);
        try {
            const payload = {
                name: audienceName,
                contacts: tableData,
            };
            const response = await saveAudience(payload);
            if (response) {
                setIsLoading(false);
                setUploadedCsvData([]);
                setAudienceName("");
                initialFetchSettings();
            }

        } catch (error) {
            setIsLoading(false);
            Toast.error(error.getApiErrorDetails().message);
        }
    };

    return (
        <>
            <CardTableShell maxWidth="1112px" minHeight="963px">
                <CardTableToolbar>
                    <FlexContainer>
                        <TextLabel fontSize="20px" fontWeight="bold">
                            Review Imported Contacts
                        </TextLabel>
                        <FlexContainer maxWidth="614px">
                            <TextLabel fontSize="15px" fontWeight="normal" color="#612684">
                                Below is a preview of the contacts imported from your CSV file.
                                Review the details before saving this list. You can edit or remove
                                individual contacts if needed.
                            </TextLabel>
                        </FlexContainer>
                    </FlexContainer>
                    <HeaderCard
                        title={audienceName}
                        subtitle={"Contacts Available"}
                        quantity={uploadedCsvData && newContactList.length}
                    />
                </CardTableToolbar>
                <CardTableToolbar>
                    <CardTableSelectAllControl>
                        <label>
                            <Checkbox
                                checked={selectAllItem}
                                onChange={() => handleMainCheckbox()}
                                onClick={(e) => e.stopPropagation()}
                            />
                        </label>
                    </CardTableSelectAllControl>
                    <FlexContainer direction="row" tDirection="row" gap="15px">
                        {selectedItem.length > 0 && (
                            <ActionButton
                                type="button"
                                width="100%"
                                height="39px"
                                color="#FF3B3B"
                                border="1px solid #FF3B3B"
                                backgroundColor="#FFF5F5"
                                onClick={() => handleDeleteSelectedItems()}
                            >
                                Delete Contact
                            </ActionButton>
                        )}
                        <input
                            type="file"
                            accept=".csv"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            onChange={(e) => {
                                handleCsvUpload(e);
                                e.target.value = ""; // ✅ reset file input
                            }}
                        />
                        <ActionButton
                            type="button"
                            width="100%"
                            height="39px"
                            color="#FFFFFF"
                            backgroundColor="#171A22"
                            border="0px"
                            onClick={onImportCsvClick}
                        >
                            Re-upload CSV
                        </ActionButton>

                        <ActionButton
                            type="button"
                            width="100%"
                            height="39px"
                            color="#FFFFFF"
                            backgroundColor="#171A22"
                            border="0px"
                            onClick={() => toggleAddSingleContact()}
                        >
                            Add Another Contact
                        </ActionButton>
                    </FlexContainer>
                </CardTableToolbar>
                <CardTableRows maxHeight="44rem" minHeight="36rem">
                    <Rows
                        tableData={tableData}
                        onToggle={(contact) => toggleItemSelection(contact)}
                        isSelected={isItemSelected}
                        isViewingContacts={false}
                        countryCode={countryCode}
                        onSort={handleSortColumn}
                        isSortable={isASortableColumn}
                    />
                </CardTableRows>

                <FlexContainer
                    direction="row"
                    tDirection="row"
                    style={{ justifyContent: "flex-end" }}
                    padding="20px 20px 30px 0px"
                    gap="10px"
                >
                    <FlexContainer width="140px" mWidth="100%">
                        <ActionButton
                            type="button"
                            width="100%"
                            height="39px"
                            color="#000000"
                            backgroundColor="#FFFFFF"
                            onClick={() => setUploadedCsvData([])}
                        >
                            Cancel
                        </ActionButton>
                    </FlexContainer>
                    <FlexContainer width="auto" mWidth="100%">
                        {isLoading ? (
                            <LoadingIndicatorButton>
                                <LottieLoadingIndicator
                                    height="2rem"
                                    width="2rem"
                                    margin="5px"
                                    containerHeight="2rem"
                                />
                            </LoadingIndicatorButton>
                        ) : (
                            <ActionButton
                                type="button"
                                width="100%"
                                height="39px"
                                color="#FFFFFF"
                                backgroundColor={enableButton ? "#171A22" : "#99C2FF"}
                                border="0px"
                                onClick={enableButton ? () => onSaveAudienceAndContacts() : () => { }}
                            >
                                Save Audience & Contacts
                            </ActionButton>
                        )}
                    </FlexContainer>
                </FlexContainer>
            </CardTableShell>
            {showAddSingleContact && (
                <AddSingleContactForm
                    toggleAddSingleContact={toggleAddSingleContact}
                    contactFormData={contactFormData}
                    setContactFormData={setContactFormData}
                    isEditing={true}
                    onSaveSingleContact={onSaveSingleContact}
                />
            )}
        </>
    );
};
